import { memo, useEffect, useMemo } from "react"
import type { FC } from "react"

import type { WeatherServicePlotDef } from "types/dashboard.types"
import type { EntityType } from "types/common.types"

import useDateRange from "helpers/hooks/useDateRange"
import { getCellHeight } from "helpers/utils/dashboards"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { useWeatherServiceQuery } from "features/api"
import { isWithinOneDay } from "helpers/utils/dateUtils"

import Plot from "../Plot"
import { formatTelemetryData, getFill } from "../helpers"

const PlotMemo = memo(Plot)

interface WeatherServicePlotProps {
  def: WeatherServicePlotDef
  geohashes: string[]
  entityIds: number[]
}

const WeatherServicePlot: FC<WeatherServicePlotProps> = ({
  def,
  geohashes,
  entityIds,
}) => {
  const { view, reqConfig, dataSource } = def
  const {
    config,
    plotLayout,
    dispatchPlotLayout,
    entityType: contextEntityType,
  } = useDashboardContext()

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools(contextEntityType ?? dataSource.entity),
    [getDateRangeTools, contextEntityType, dataSource.entity],
  )

  const {
    aggregate,
    aggregate_n,
    aggregate_use_original_time,
    fields,
    group,
    method,
    aggregate_every,
    measurement,
    allowRawData,
  } = reqConfig

  const queryParams = {
    aggregate,
    aggregate_n,
    aggregate_use_original_time,
    method,
    group,
    aggregate_every,
    measurement,
    field: fields,
    from_date: dateRange?.fromDate,
    to_date: dateRange?.toDate,
    geohash: geohashes,
  }

  const { data, error, isFetching, refetch } = useWeatherServiceQuery(
    {
      params: queryParams,
    },
    {
      skip:
        !isDateRangeValid || !dateRange || !fields || !fields.length || !geohashes.length,
    },
  )

  const formattedDef = useMemo(() => {
    if (view.type !== "xy") return def
    const fill = getFill(view, reqConfig, undefined, geohashes)
    return { ...def, view: { ...view, properties: { ...view.properties, fill } } }
  }, [def, view, reqConfig, geohashes])

  const formattedData = useMemo(() => formatTelemetryData(data ?? []), [data])

  const isRawData = allowRawData && dateRange != null && isWithinOneDay(dateRange)

  const shouldRender = Boolean(error || (data && data.length !== 0))

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: contextEntityType ?? dataSource.entity,
      entityIds,
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [
      contextEntityType,
      dataSource.entity,
      entityIds,
      dateRange?.fromDate,
      dateRange?.toDate,
    ],
  )

  useEffect(() => {
    if (dispatchPlotLayout && config) {
      dispatchPlotLayout({
        type: "UPDATE_CELL_HEIGHT",
        payload: {
          id: view.nameKey,
          h: shouldRender ? getCellHeight(view.nameKey, config.type) : 0,
        },
      })
    }
  }, [shouldRender, view.nameKey, dispatchPlotLayout, config])

  const render = Boolean(
    (error || (shouldRender && formattedData && formattedData.length > 0)) &&
      (plotLayout === null || plotLayout?.[view.nameKey]?.h !== 0),
  )

  return (
    <>
      {render && (
        <PlotMemo
          data={formattedData}
          def={formattedDef}
          csvData={csvData}
          entityData={{ entity: dataSource.entity, ids: entityIds }}
          isRawData={isRawData}
          error={error}
          isFetching={isFetching}
          refetch={refetch}
        />
      )}
    </>
  )
}

export default WeatherServicePlot
